import { useState } from 'react';
import { Edit, Trash2, Camera, CameraOff } from 'lucide-react';
import { CostImagesSection } from './CostImagesSection';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import type { JobCost } from '../types/jobCost';

interface CostListTableProps {
  jobId: number;
  costs: JobCost[];
  onEdit: (cost: JobCost) => void;
  onDelete: (costId: number) => void;
  isDeleting?: boolean;
}

export function CostListTable({ jobId, costs, onEdit, onDelete, isDeleting }: CostListTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [costToDelete, setCostToDelete] = useState<number | null>(null);
  const [imagesDialogOpen, setImagesDialogOpen] = useState(false);
  const [selectedCostForImages, setSelectedCostForImages] = useState<JobCost | null>(null);

  const handleOpenImages = (cost: JobCost) => {
    setSelectedCostForImages(cost);
    setImagesDialogOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  const handleDeleteClick = (costId: number) => {
    setCostToDelete(costId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (costToDelete !== null) {
      onDelete(costToDelete);
      setDeleteDialogOpen(false);
      setCostToDelete(null);
    }
  };

  // Sort costs by date descending (most recent first)
  const sortedCosts = [...costs].sort((a, b) => {
    return new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime();
  });

  if (costs.length === 0) {
    return (
      <div className="bg-white rounded-[20px] border border-[#d9d9d9] p-8 text-center">
        <p className="text-gray-500">No costs recorded yet.</p>
        <p className="text-sm text-gray-400 mt-1">Click "Add Cost" to record a new cost.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-[20px] border border-[#d9d9d9] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Date</TableHead>
              <TableHead className="w-[150px]">Category</TableHead>
              <TableHead className="w-[180px]">Paid To</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right w-[100px]">Amount</TableHead>
              <TableHead className="text-right w-[100px]">Tax</TableHead>
              <TableHead className="text-right w-[100px]">Total</TableHead>
              <TableHead className="w-[80px] text-center">Images</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedCosts.map((cost) => (
              <TableRow key={cost.id}>
                <TableCell className="text-sm">{formatDate(cost.paymentDate)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {cost.costCategoryName}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{cost.paidTo}</TableCell>
                <TableCell className="text-sm text-gray-600">
                  {truncate(cost.description, 50)}
                </TableCell>
                <TableCell className="text-right text-sm font-medium">
                  {formatCurrency(cost.amount)}
                </TableCell>
                <TableCell className="text-right text-sm">
                  {formatCurrency(cost.taxAmount)}
                </TableCell>
                <TableCell className="text-right text-sm font-semibold">
                  {formatCurrency(cost.total)}
                </TableCell>
                <TableCell className="text-center">
                  {(cost.imageCount ?? 0) > 0 ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenImages(cost)}
                      className="h-8 w-8 p-0"
                      aria-label={`View ${cost.imageCount} image${cost.imageCount !== 1 ? 's' : ''} for ${cost.costCategoryName} cost`}
                    >
                      <Camera className="h-5 w-5 text-[#0A0A9E]" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenImages(cost)}
                      className="h-8 w-8 p-0"
                      aria-label={`Add images for ${cost.costCategoryName} cost`}
                    >
                      <CameraOff className="h-5 w-5 text-gray-300" />
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(cost)}
                      className="h-8 w-8 p-0"
                      aria-label={`Edit ${cost.costCategoryName} cost`}
                    >
                      <Edit className="h-4 w-4 text-[#0A0A9E]" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(cost.id)}
                      className="h-8 w-8 p-0"
                      aria-label={`Delete ${cost.costCategoryName} cost`}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Cost"
        message="Are you sure you want to delete this cost? This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />

      <Dialog open={imagesDialogOpen} onOpenChange={setImagesDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Cost Images - {selectedCostForImages?.costCategoryName}
            </DialogTitle>
          </DialogHeader>
          {selectedCostForImages && (
            <CostImagesSection
              jobId={jobId}
              costId={selectedCostForImages.id}
              canDelete={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
